import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const STATUS_STYLES = {
  pending:  { bg: 'rgba(245,196,0,0.12)',  color: '#f5c400',  label: '⏳ Pending'  },
  approved: { bg: 'rgba(16,185,129,0.12)', color: '#10b981', label: '✅ Approved' },
  rejected: { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444',  label: '❌ Rejected' },
};

export default function AdminGames() {
  const [tab, setTab] = useState('settings');
  const [redeems, setRedeems] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [roundsModal, setRoundsModal] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [settings, setSettings] = useState({ minRedeem: 1000, redeemRate: 100, gamesEnabled: true });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [creditForm, setCreditForm] = useState({ userId: '', username: '', amount: '', note: '' });
  const [creditSaving, setCreditSaving] = useState(false);
  const [creditMsg, setCreditMsg] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);

  const loadRedeems = () => api.get('/games/admin/redeems').then(({ data }) => setRedeems(data.redeems || []));
  const loadSessions = () => api.get('/games/admin/records').then(({ data }) => setSessions(data.sessions || []));
  const searchUsers = async (q) => {
    if (!q) { setUserResults([]); return; }
    try {
      const { data } = await api.get('/admin/users');
      setUserResults((data.users || []).filter(u => u.username.toLowerCase().includes(q.toLowerCase())).slice(0, 5));
    } catch {}
  };

  const handleAddCredits = async () => {
    if (!creditForm.userId || !creditForm.amount) return;
    setCreditSaving(true); setCreditMsg(null);
    try {
      await api.post('/games/admin/add-credits', { userId: creditForm.userId, amount: Number(creditForm.amount), note: creditForm.note });
      setCreditMsg({ type: 'success', text: `✅ $${creditForm.amount} credits added to ${creditForm.username}!` });
      setCreditForm({ userId: '', username: '', amount: '', note: '' });
      setUserResults([]);
      setUserSearch('');
      setTimeout(() => { setCreditModal(false); setCreditMsg(null); }, 2500);
    } catch (err) {
      setCreditMsg({ type: 'error', text: err.response?.data?.message || 'Error adding credits' });
    } finally { setCreditSaving(false); }
  };

  const loadSettings = () => api.get('/games/settings').then(({ data }) => {
    if (data.settings) setSettings({ minRedeem: data.settings.minRedeem, redeemRate: data.settings.redeemRate, gamesEnabled: data.settings.gamesEnabled });
  });

  useEffect(() => {
    Promise.all([loadRedeems(), loadSessions(), loadSettings()]).finally(() => setLoading(false));
  }, []);

  const loadRounds = async (userId) => {
    try {
      const { data } = await api.get(`/games/admin/rounds/${userId}`);
      setRounds(data.rounds || []);
    } catch { setRounds([]); }
  };

  const handleProcess = async (id, status) => {
    setUpdating(true);
    try {
      await api.put(`/games/admin/redeems/${id}`, { status, adminNote });
      await loadRedeems();
      setSelected(null); setAdminNote('');
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
    finally { setUpdating(false); }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true); setSettingsMsg(null);
    try {
      await api.put('/games/admin/settings', settings);
      setSettingsMsg({ type: 'success', text: '✅ Settings saved successfully!' });
    } catch (err) {
      setSettingsMsg({ type: 'error', text: err.response?.data?.message || 'Error saving' });
    } finally { setSavingSettings(false); setTimeout(() => setSettingsMsg(null), 3000); }
  };

  const counts = {
    pending:  redeems.filter(r => r.status === 'pending').length,
    approved: redeems.filter(r => r.status === 'approved').length,
  };

  const totalFunWon = sessions.reduce((s, sess) => s + (sess.totalWon || 0), 0);
  const totalGames  = sessions.reduce((s, sess) => s + (sess.gamesPlayed || 0), 0);

  // Preview conversion table based on current rate
  const previewTable = [1000, 2000, 5000, 10000, 50000, 100000].map(fc => ({
    fc, fp: Math.floor((fc / 1000) * (settings.redeemRate / 10))
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>GAME CENTER</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Manage mini games, redeem requests, and game sessions</p>
        </div>
        <button onClick={() => setCreditModal(true)} className="btn btn-secondary btn-sm" style={{ marginRight: 8 }}>
          🎰 Add Credits to User
        </button>
        {counts.pending > 0 && (
          <div style={{ background: 'rgba(245,196,0,0.12)', border: '1px solid #f5c400', borderRadius: 10, padding: '8px 16px', color: '#f5c400', fontWeight: 700, fontSize: 14, animation: 'pulse 2s infinite' }}>
            🔔 {counts.pending} redeem{counts.pending > 1 ? 's' : ''} pending
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Games Status',     value: settings.gamesEnabled ? 'ONLINE' : 'OFFLINE', icon: settings.gamesEnabled ? '🟢' : '🔴', color: settings.gamesEnabled ? '#10b981' : '#ef4444' },
          { label: 'Total Sessions',   value: sessions.length,                              icon: '🎰', color: '#f5c400' },
          { label: 'Total Games',      value: totalGames,                                   icon: '🎮', color: '#c9a000' },
          { label: 'Fun Credits Won',  value: `$${totalFunWon.toLocaleString()}`,           icon: '💰', color: '#10b981' },
          { label: 'Pending Redeems',  value: counts.pending,                               icon: '💎', color: '#7c3aed' },
        ].map(s => (
          <div key={s.label} className="card" style={{ borderLeft: `3px solid ${s.color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>{s.label}</span>
              <span style={{ fontSize: 20 }}>{s.icon}</span>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[['settings','⚙️ Settings'],['redeems','💎 Redeems'],['sessions','🎰 Sessions']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} className={`btn btn-sm ${tab === key ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
        ))}
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
        <>
          {/* ── SETTINGS TAB ── */}
          {tab === 'settings' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div className="card" style={{ padding: 28 }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: '#f5c400', marginBottom: 20 }}>GAME SETTINGS</h3>

                {settingsMsg && (
                  <div style={{ background: settingsMsg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${settingsMsg.type === 'success' ? '#10b981' : '#ef4444'}`, borderRadius: 8, padding: '10px 14px', color: settingsMsg.type === 'success' ? '#10b981' : '#ef4444', marginBottom: 16, fontSize: 14 }}>
                    {settingsMsg.text}
                  </div>
                )}

                {/* Toggle games on/off */}
                <div style={{ background: settings.gamesEnabled ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${settings.gamesEnabled ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: settings.gamesEnabled ? '#10b981' : '#ef4444' }}>
                      {settings.gamesEnabled ? '🟢 Mini Games ONLINE' : '🔴 Mini Games OFFLINE'}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                      {settings.gamesEnabled ? 'Users can play and collect daily credits' : 'Games are hidden and disabled for all users'}
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, gamesEnabled: !settings.gamesEnabled })}
                    style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: settings.gamesEnabled ? '#ef4444' : '#10b981', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                    {settings.gamesEnabled ? '🔴 Turn OFF' : '🟢 Turn ON'}
                  </button>
                </div>

                <div className="form-group">
                  <label>Minimum Redeem (Fun Credits)</label>
                  <input type="number" value={settings.minRedeem} onChange={e => setSettings({ ...settings, minRedeem: +e.target.value })} min="100" step="100" />
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>Minimum fun credits needed to submit a redeem request</span>
                </div>

                <div className="form-group">
                  <label>Freeplay Rate ($ per 1,000 FC)</label>
                  <input type="number" value={settings.redeemRate} onChange={e => setSettings({ ...settings, redeemRate: +e.target.value })} min="1" step="1" />
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                    Current: $1,000 FC = ${(settings.redeemRate / 10).toFixed(2)} Freeplay
                  </span>
                </div>

                <button onClick={handleSaveSettings} disabled={savingSettings} className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}>
                  {savingSettings ? '⟳ Saving...' : '💾 Save Settings'}
                </button>
              </div>

              {/* Conversion Preview */}
              <div className="card" style={{ padding: 28 }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: '#f5c400', marginBottom: 20 }}>CONVERSION PREVIEW</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>Based on your current rate settings:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {previewTable.map(({ fc, fp }) => (
                    <div key={fc} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: fc === settings.minRedeem ? 'rgba(245,196,0,0.1)' : 'var(--bg-secondary)', border: `1px solid ${fc === settings.minRedeem ? '#f5c400' : 'var(--border)'}`, borderRadius: 8 }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                        ${fc.toLocaleString()} FC
                        {fc === settings.minRedeem && <span style={{ color: '#f5c400', fontSize: 11, marginLeft: 6 }}>← MIN</span>}
                      </span>
                      <span style={{ color: '#10b981', fontWeight: 700, fontSize: 15 }}>= ${fp} Freeplay</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(245,196,0,0.06)', border: '1px solid rgba(245,196,0,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  💡 To change: 1k = $1 freeplay, set rate to <strong style={{ color: '#f5c400' }}>10</strong>. For 1k = $10, set rate to <strong style={{ color: '#f5c400' }}>100</strong>.
                </div>
              </div>
            </div>
          )}

          {/* ── REDEEMS TAB ── */}
          {tab === 'redeems' && (
            <div>
              {redeems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>💎</div><p>No redeem requests yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {redeems.map(r => (
                    <div key={r._id} className="card" style={{ borderLeft: `3px solid ${STATUS_STYLES[r.status]?.color}` }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr auto', gap: 20, alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: '#0a0900' }}>{r.user?.username?.[0]?.toUpperCase()}</div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 15 }}>{r.user?.username}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.user?.email}</div>
                            </div>
                          </div>
                          {r.gameUsername && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>🎮 <span style={{ color: '#f5c400', fontWeight: 600 }}>{r.gameUsername}</span></div>}
                          {r.platform && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Platform: {r.platform}</div>}
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{new Date(r.createdAt).toLocaleString()}</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em', marginBottom: 4 }}>FUN CREDITS</div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 900, color: '#f5c400' }}>${r.funCredits?.toLocaleString()}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{r.session?.date}</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em', marginBottom: 4 }}>FREEPLAY</div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 900, color: '#10b981' }}>${r.freeplayValue}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', minWidth: 110 }}>
                          <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: STATUS_STYLES[r.status]?.bg, color: STATUS_STYLES[r.status]?.color, whiteSpace: 'nowrap' }}>{STATUS_STYLES[r.status]?.label}</span>
                          {r.status === 'pending' && <button className="btn btn-primary btn-sm" onClick={() => { setSelected(r); setAdminNote(''); }} style={{ width: '100%' }}>Review →</button>}
                          {r.adminNote && <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', maxWidth: 120 }}>📝 {r.adminNote}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── SESSIONS TAB ── */}
          {tab === 'sessions' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                    {['User','Date','Balance','Total Won','Games','Status','Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sessions.map(s => (
                    <tr key={s._id} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{s.user?.username}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.user?.email}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{s.date}</td>
                      <td style={{ padding: '12px 16px', fontFamily: 'var(--font-display)', fontWeight: 700, color: s.currentBalance > 0 ? '#f5c400' : '#555' }}>${s.currentBalance}</td>
                      <td style={{ padding: '12px 16px', fontFamily: 'var(--font-display)', fontWeight: 700, color: s.totalWon >= settings.minRedeem ? '#10b981' : '#f5c400' }}>${s.totalWon?.toLocaleString()}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 13 }}>{s.gamesPlayed}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                          background: s.status==='redeemed'?'rgba(16,185,129,0.12)':s.status==='expired'?'rgba(100,100,100,0.12)':'rgba(245,196,0,0.12)',
                          color: s.status==='redeemed'?'#10b981':s.status==='expired'?'#666':'#f5c400' }}>
                          {s.status==='redeemed'?'✅ Redeemed':s.status==='expired'?'⏸ Expired':'🎮 Active'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setRoundsModal(s.user); loadRounds(s.user._id); }}>View Rounds</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sessions.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>No game sessions yet</div>}
            </div>
          )}
        </>
      )}

      {/* Review Redeem Modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 20, color: '#f5c400' }}>Process Redeem</h2>
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[['User',selected.user?.username],['Date',selected.session?.date],['Fun Credits',`$${selected.funCredits?.toLocaleString()}`],['Freeplay Value',`$${selected.freeplayValue}`],['Game Username',selected.gameUsername||'—'],['Platform',selected.platform||'—']].map(([k,v])=>(
                  <div key={k} style={{ background:'var(--bg-card)',borderRadius:8,padding:'8px 12px' }}>
                    <div style={{ fontSize:10,color:'var(--text-muted)',marginBottom:2 }}>{k}</div>
                    <div style={{ fontWeight:700,fontSize:14,color:'#f5c400' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Message to User (sent via Live Chat)</label>
              <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)} rows={3} placeholder={`e.g. Your $${selected.freeplayValue} freeplay has been loaded! Enjoy!`} />
            </div>
            <div style={{ background:'rgba(245,196,0,0.06)',border:'1px solid rgba(245,196,0,0.2)',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'var(--text-secondary)' }}>
              💬 User will receive a message via Live Chat automatically.
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => handleProcess(selected._id, 'approved')} disabled={updating} style={{ flex:1,padding:'14px',background:'rgba(16,185,129,0.15)',border:'2px solid #10b981',borderRadius:10,color:'#10b981',fontWeight:700,cursor:'pointer',fontSize:15,fontFamily:'var(--font-body)' }}>{updating?'⏳...':'✅ Approve & Notify'}</button>
              <button onClick={() => handleProcess(selected._id, 'rejected')} disabled={updating} style={{ flex:1,padding:'14px',background:'rgba(239,68,68,0.1)',border:'2px solid #ef4444',borderRadius:10,color:'#ef4444',fontWeight:700,cursor:'pointer',fontSize:15,fontFamily:'var(--font-body)' }}>{updating?'⏳...':'❌ Reject'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Rounds Modal */}
      {roundsModal && (
        <div className="modal-overlay" onClick={() => setRoundsModal(null)}>
          <div className="modal" style={{ maxWidth: 660, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setRoundsModal(null)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 20 }}>Game Rounds — {roundsModal.username}</h2>
            {rounds.length === 0 ? (
              <div style={{ textAlign:'center',padding:40,color:'var(--text-secondary)' }}>No rounds recorded</div>
            ) : (
              <table style={{ width:'100%',borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid var(--border)',background:'var(--bg-secondary)' }}>
                    {['Game','Bet','Win','Result','Balance','Time'].map(h=>(
                      <th key={h} style={{ padding:'10px 14px',textAlign:'left',fontSize:11,color:'var(--text-secondary)',fontFamily:'var(--font-display)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rounds.map(r=>(
                    <tr key={r._id} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'10px 14px',textTransform:'capitalize',fontSize:14 }}>{r.game}</td>
                      <td style={{ padding:'10px 14px',color:'#ef4444',fontWeight:700 }}>-${r.betAmount}</td>
                      <td style={{ padding:'10px 14px',color:r.winAmount>0?'#10b981':'#555',fontWeight:700 }}>{r.winAmount>0?`+$${r.winAmount}`:'—'}</td>
                      <td style={{ padding:'10px 14px' }}>
                        <span style={{ fontSize:12,padding:'2px 8px',borderRadius:8,background:r.result==='win'?'rgba(16,185,129,0.12)':r.result==='push'?'rgba(100,100,100,0.12)':'rgba(239,68,68,0.12)',color:r.result==='win'?'#10b981':r.result==='push'?'#888':'#ef4444' }}>{r.result}</span>
                      </td>
                      <td style={{ padding:'10px 14px',fontFamily:'var(--font-display)',color:'#f5c400' }}>${r.balanceAfter}</td>
                      <td style={{ padding:'10px 14px',fontSize:12,color:'var(--text-muted)' }}>{new Date(r.createdAt).toLocaleTimeString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Add Credits Modal */}
      {creditModal && (
        <div className="modal-overlay" onClick={() => setCreditModal(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setCreditModal(false)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 8, color: '#f5c400' }}>🎰 ADD MINI GAME CREDITS</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>Manually add Fun Credits to a user's Mini Games balance. User will be notified via chat and notifications.</p>

            {creditMsg && (
              <div style={{ background: creditMsg.type==='success'?'rgba(16,185,129,0.1)':'rgba(239,68,68,0.1)', border: `1px solid ${creditMsg.type==='success'?'#10b981':'#ef4444'}`, borderRadius: 8, padding: '10px 14px', color: creditMsg.type==='success'?'#10b981':'#ef4444', marginBottom: 16, fontSize: 14 }}>{creditMsg.text}</div>
            )}

            <div className="form-group" style={{ position: 'relative' }}>
              <label>Search User *</label>
              <input
                value={userSearch}
                onChange={e => { setUserSearch(e.target.value); searchUsers(e.target.value); }}
                placeholder="Type username..."
              />
              {userResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 100, overflow: 'hidden', marginTop: 4 }}>
                  {userResults.map(u => (
                    <div key={u._id} onClick={() => { setCreditForm({ ...creditForm, userId: u._id, username: u.username }); setUserSearch(u.username); setUserResults([]); }}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 14 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontWeight: 600 }}>{u.username}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{u.email}</span>
                    </div>
                  ))}
                </div>
              )}
              {creditForm.username && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#10b981' }}>✅ Selected: {creditForm.username}</div>
              )}
            </div>

            <div className="form-group">
              <label>Amount (Fun Credits) *</label>
              <input type="number" min="1" value={creditForm.amount} onChange={e => setCreditForm({ ...creditForm, amount: e.target.value })} placeholder="e.g. 500" />
            </div>

            <div className="form-group">
              <label>Note to User <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input value={creditForm.note} onChange={e => setCreditForm({ ...creditForm, note: e.target.value })} placeholder="e.g. Welcome bonus, compensation, etc." />
            </div>

            <div style={{ background: 'rgba(245,196,0,0.06)', border: '1px solid rgba(245,196,0,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
              💬 User will receive a notification + Live Chat message automatically.
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={handleAddCredits} disabled={creditSaving || !creditForm.userId || !creditForm.amount} className="btn btn-primary" style={{ flex: 1, opacity: (!creditForm.userId || !creditForm.amount) ? 0.5 : 1 }}>
                {creditSaving ? '⟳ Adding...' : '✅ Add Credits'}
              </button>
              <button onClick={() => { setCreditModal(false); setCreditForm({ userId:'', username:'', amount:'', note:'' }); setUserSearch(''); setUserResults([]); setCreditMsg(null); }} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
